import { useTranslation } from "react-i18next";

import { useDemandEvents } from "@/api/demandForecast";
import type { ExcludedEventDetail } from "@/api/demandForecast";
import { LoadingState } from "@/components/states";
import {
  Empty,
  EmptyDescription,
  EmptyHeader,
  EmptyTitle,
} from "@/components/ui/empty";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { formatDate, formatNumber } from "@/utils/formatters";

interface CleansingDrawerProps {
  item: number;
  location: number;
  open: boolean;
  onClose: (value: boolean) => void;
}

export function CleansingDrawer({
  item,
  location,
  open,
  onClose,
}: CleansingDrawerProps) {
  const { t } = useTranslation();
  const { data, isLoading } = useDemandEvents(item, location);
  const events: ExcludedEventDetail[] = data ?? [];

  function formatFactor(factor: number | null): string {
    return factor === null
      ? "—"
      : `${formatNumber(factor, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })}× ${t("items.detail.cleansing.factorUnit")}`;
  }

  return (
    <Sheet open={open} onOpenChange={onClose}>
      <SheetContent className="flex flex-col gap-4">
        <SheetHeader>
          <SheetTitle>{t("items.detail.cleansing.drawerTitle")}</SheetTitle>
        </SheetHeader>

        <div className="flex-1 overflow-auto px-4">
          {isLoading ? (
            <LoadingState label={t("items.detail.cleansing.loading")} />
          ) : events.length === 0 ? (
            <Empty>
              <EmptyHeader>
                <EmptyTitle>
                  {t("items.detail.cleansing.emptyTitle")}
                </EmptyTitle>
                <EmptyDescription>
                  {t("items.detail.cleansing.emptyDescription")}
                </EmptyDescription>
              </EmptyHeader>
            </Empty>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>
                    {t("items.detail.cleansing.columns.date")}
                  </TableHead>
                  <TableHead>
                    {t("items.detail.cleansing.columns.segment")}
                  </TableHead>
                  <TableHead>
                    {t("items.detail.cleansing.columns.quantity")}
                  </TableHead>
                  <TableHead>
                    {t("items.detail.cleansing.columns.factor")}
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {events.map((event) => (
                  <TableRow key={event.code}>
                    <TableCell>{formatDate(event.date)}</TableCell>
                    <TableCell>{event.segment ?? ""}</TableCell>
                    <TableCell>{formatNumber(event.quantity)}</TableCell>
                    <TableCell>{formatFactor(event.factor)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
